import { hexToRGBA } from '@/config/settings';

import type { ChartTheme } from './types';

/** Opacity of the zoom selection box. The edge shares it, so the box reads as one shape. */
const ZOOM_DRAG_ALPHA = 0.3;

/**
 * The `zoom.zoom.drag` colours of chartjs-plugin-zoom for every chart in the app. In light
 * this is the grey box with a dark edge the charts always drew.
 */
export function chartZoomDrag(theme: ChartTheme): { backgroundColor: string; borderColor: string } {
    return {
        backgroundColor: hexToRGBA(theme.zoom.fill, ZOOM_DRAG_ALPHA),
        borderColor: hexToRGBA(theme.zoom.border, ZOOM_DRAG_ALPHA),
    };
}

/** WCAG relative luminance of a 6-digit hex colour. */
function luminance(hex: string): number {
    const channels = [1, 3, 5].map((start) => {
        const value = Number.parseInt(hex.slice(start, start + 2), 16) / 255;
        return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
}

function contrast(a: string, b: string): number {
    const [light, dark] = [luminance(a), luminance(b)].sort((x, y) => y - x);
    return (light + 0.05) / (dark + 0.05);
}

const HEX6 = /^#[0-9a-f]{6}$/i;

/**
 * The more readable of two inks on `fill`: for a label drawn inside a coloured bar, where the
 * bar's colour decides whether dark or light text can be read. Anything that is not a 6-digit
 * hex gets `dark`, the ink the labels always used.
 */
export function readableInkOn(fill: string, dark: string, light: string): string {
    if (!HEX6.test(fill) || !HEX6.test(dark) || !HEX6.test(light)) return dark;
    return contrast(fill, dark) >= contrast(fill, light) ? dark : light;
}
